import { useCallback, useEffect, useState } from 'react'
import { off, onValue, ref, set, remove, serverTimestamp } from 'firebase/database'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { auth, db } from '../firebase'
import { get } from 'lodash'

const SIZE = 30

function getRandomColor () {
  const letters = '0123456789ABCD'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 14)]
  }
  return color
}

const MOVES = {
  37: (p) => [p[0], p[1] - 1], // Left arrow
  38: (p) => [p[0] - 1, p[1]], // Up arrow
  39: (p) => [p[0], p[1] + 1], // Right arrow
  40: (p) => [p[0] + 1, p[1]], // Down arrow
  65: (p) => [p[0], p[1] - 1], // A key (left)
  87: (p) => [p[0] - 1, p[1]], // W key (up)
  68: (p) => [p[0], p[1] + 1], // D key (right)
  83: (p) => [p[0] + 1, p[1]]  // S key (down)
}

const isOpen = (p, data) => {
  if (p[0] < 0 || p[0] >= SIZE || p[1] < 0 || p[1] >= SIZE) {
    return false
  }
  let noConflict = true
  Object.values(data).forEach(v => {
    if (v[0] === p[0] && v[1] === p[1]) {
      noConflict = false
    }
  })
  return noConflict
}

const useBounce = () => {
  const uid = get(auth, 'currentUser.uid')
  const isAnonymous = get(auth, 'currentUser.isAnonymous')
  const refPath = 'bounce'

  let bounceRef
  const [data, setData] = useState([])
  const [color, setColor] = useState()
  const [target, setTarget] = useState(null)
  const [resetTime, setResetTime] = useState(null)

  useEffect(() => {
    const get = () => {
      bounceRef = ref(db, refPath)
      onValue(bounceRef, (item) => {
        const bounceData = item.val()
        setData(bounceData)
        
        // Update color from database if player exists
        if (bounceData && bounceData[uid]) {
          setColor(bounceData[uid][2])
        }
      })
    }
    get()

    return () => {
      if (bounceRef) { // close the connection
        off(bounceRef)
      }
    }
  }, [uid])

  // Listen to target changes
  useEffect(() => {
    const targetRef = ref(db, 'bounce-target')
    onValue(targetRef, (snapshot) => {
      const targetData = snapshot.val()
      if (targetData) {
        setTarget({
          position: [targetData[0], targetData[1]],
          timestamp: targetData[2]
        })
      } else {
        setTarget(null)
      }
    })

    return () => {
      off(targetRef)
    }
  }, [])

  // Listen to reset time changes
  useEffect(() => {
    const resetTimeRef = ref(db, 'bounce-reset-time')
    onValue(resetTimeRef, (snapshot) => {
      const time = snapshot.val()
      setResetTime(time)
    })

    return () => {
      off(resetTimeRef)
    }
  }, [])

  // Check if player collected target and update score
  const checkTargetCollection = useCallback((newPosition) => {
    if (!target) return false

    const [newRow, newCol] = newPosition
    const [targetRow, targetCol] = target.position

    if (newRow === targetRow && newCol === targetCol) {
      // Player collected the target
      const currentPlayer = data[uid]
      if (currentPlayer) {
        const currentScore = currentPlayer[3] || 0
        const newScore = currentScore + 10
        
        // Update player data with new score and server timestamp
        set(ref(db, `bounce/${uid}`), [...newPosition, color, newScore, serverTimestamp()])
        
        // Remove the target (will trigger server to spawn new one)
        remove(ref(db, 'bounce-target'))
        
        return true
      }
    }
    return false
  }, [target, data, uid, color])

  const leave = () => {
    remove(ref(db, `bounce/${uid}`))
  }

  const setValue = useCallback((value) => {
    set(ref(db, `bounce/${uid}`), value)
  }, [uid, color])

  const move = useCallback((keyCode) => {
    if (!MOVES[keyCode]) {
      return
    }
    const current = data[uid]
    if (!current) {

    } else {
      const potentialMove = MOVES[keyCode](current)
      if (isOpen(potentialMove, data)) {
        // Check if this move collects a target
        const collected = checkTargetCollection(potentialMove)
        
        if (!collected) {
          // Normal move without collection - use server timestamp
          const currentScore = current[3] || 0
          setValue([...potentialMove, color, currentScore, serverTimestamp()])
        }
        // If collected, checkTargetCollection already updated the position and score
      } else {
        console.log('requested move not open')
      }
    }
  }, [data, uid, color, checkTargetCollection, setValue])

  const join = useCallback(async () => {
    // Check if user is authenticated (not anonymous)
    if (isAnonymous) {
      alert('You must be signed in with a non-anonymous account to play')
      return
    }

    try {
      const functions = getFunctions()
      const joinGame = httpsCallable(functions, 'joinBounceGameV2')
      await joinGame()
      // The function sets the data in Firebase, so we don't need to do anything else
      // The onValue listener will pick up the change
    } catch (error) {
      console.error('Error joining game:', error)
      alert(`Failed to join game: ${error.message}`)
    }
  }, [isAnonymous])

  return {
    join,
    move,
    leave,
    data,
    color,
    target,
    resetTime
  }
}

export default useBounce
